import { useEffect } from 'react'

type ScrollPosition = {
  x: number
  y: number
}

// Give up restoring once the document has stopped growing for this long —
// async content (e.g. the collection grid) is either done or stalled. This
// must comfortably exceed the slowest expected paginated fetch, since the
// timer only resets when arriving content grows the page.
const RESTORE_INACTIVITY_MS = 3000

// Hard cap on the total restore window, no matter how long content keeps
// trickling in.
const RESTORE_MAX_MS = 10000

// Marks an infinite-scroll sentinel (e.g. the collection grid's load-more
// InView). Content below it — SEO copy, the site footer — still counts
// toward document height, so scroll math must never pin the viewport past
// this element: it only triggers loads while at or below the viewport.
const SCROLL_RESTORATION_BOUNDARY_SELECTOR = '[data-scroll-restoration-boundary]'

const USER_INTERACTION_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const

function storageKey(): string {
  return `scrollPos:${window.location.pathname}${window.location.search}`
}

function saveScrollPosition(): void {
  try {
    sessionStorage.setItem(storageKey(), JSON.stringify({ x: window.scrollX, y: window.scrollY }))
  } catch {
    // sessionStorage can be unavailable (e.g. Safari private mode); losing the
    // scroll position is an acceptable fallback.
  }
}

function readScrollPosition(): ScrollPosition | undefined {
  try {
    const json = sessionStorage.getItem(storageKey())
    return json ? (JSON.parse(json) as ScrollPosition) : undefined
  } catch {
    return undefined
  }
}

function isRestorableNavigation(): boolean {
  const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]
  return navigation?.type === 'back_forward' || navigation?.type === 'reload'
}

function restoreScrollPosition(): (() => void) | undefined {
  // A hash fragment or an already-scrolled page means something else (anchor
  // scrolling, the user) owns the scroll position; don't fight it.
  if (window.location.hash || window.scrollY !== 0) return undefined

  const target = readScrollPosition()
  if (!target || target.y === 0) return undefined

  let cancelled = false
  const cancel = () => {
    cancelled = true
    USER_INTERACTION_EVENTS.forEach(event => window.removeEventListener(event, cancel))
  }
  USER_INTERACTION_EVENTS.forEach(event =>
    window.addEventListener(event, cancel, { passive: true })
  )

  const startedAt = performance.now()
  let lastGrowthAt = startedAt
  let lastHeight = document.documentElement.scrollHeight

  const attempt = () => {
    if (cancelled) return

    const height = document.documentElement.scrollHeight
    if (height > lastHeight) {
      lastHeight = height
      lastGrowthAt = performance.now()
    }

    // Scroll as far toward the target as the loaded content currently
    // allows. When the page is still too short, scrolling to the bottom of
    // the loaded content brings the infinite-scroll sentinel into view,
    // which loads the next batch and grows the document — ratcheting us
    // toward the target. Re-pinning each frame also absorbs layout shifts
    // from late-loading content until the height settles.
    const maxScrollY = height - window.innerHeight
    const boundary = document.querySelector(SCROLL_RESTORATION_BOUNDARY_SELECTOR)
    // With a boundary present, document height lies about loaded content:
    // everything below the sentinel (SEO copy, site footer) is scrollable
    // but the grid past the sentinel doesn't exist yet. Cap the pin so the
    // sentinel never rises above the viewport, and treat the target as
    // reached only once a full viewport of loaded content exists above it.
    const boundaryCap = boundary
      ? Math.max(
          0,
          Math.min(
            maxScrollY,
            boundary.getBoundingClientRect().top + window.scrollY - window.innerHeight
          )
        )
      : undefined
    const reached = boundaryCap !== undefined ? target.y <= boundaryCap : maxScrollY >= target.y

    const now = performance.now()
    const stalled = now - lastGrowthAt > RESTORE_INACTIVITY_MS
    if (reached || stalled || now - startedAt > RESTORE_MAX_MS) {
      // Terminal scroll: the exact target when it exists, otherwise as close
      // to it as the page allows.
      window.scrollTo(target.x, Math.min(target.y, maxScrollY))
      cancel()
    } else {
      window.scrollTo(target.x, Math.min(target.y, boundaryCap ?? maxScrollY))
      requestAnimationFrame(attempt)
    }
  }
  attempt()

  return cancel
}

/**
 * Restores the scroll position when a page is reached via the browser's
 * back/forward buttons through a full-document navigation.
 *
 * Next.js's `experimental.scrollRestoration` flag only restores scroll for
 * client-side (popstate) navigations within the Pages Router, and it disables
 * the browser's native restoration by setting
 * `history.scrollRestoration = 'manual'`. Returning to a Pages Router page
 * from an App Router page (e.g. collection -> PDP -> back) is a
 * full-document navigation, so neither Next.js nor the browser restores the
 * position unless the page was served from the back/forward cache. The
 * `manual` setting also suppresses the browser's native restoration on
 * reloads. This hook fills both gaps: it snapshots the position on
 * `pagehide` keyed by URL, and restores it on `back_forward` and `reload`
 * document loads.
 */
export function useScrollRestoration(): void {
  useEffect(() => {
    window.addEventListener('pagehide', saveScrollPosition)

    const cancelRestore = isRestorableNavigation() ? restoreScrollPosition() : undefined

    return () => {
      window.removeEventListener('pagehide', saveScrollPosition)
      cancelRestore?.()
    }
  }, [])
}
